import React, { useState, useEffect, useMemo } from 'react';
import {
  Download,
  Filter,
  Search,
  TrendingUp,
  TrendingDown,
  Package,
  Calendar,
  User,
  Store,
  Eye,
  Plus,
  RefreshCw,
  Truck,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { stockMovementsAPI, productsAPI, storesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernActionBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import AddProductForm from '../components/forms/AddProductForm';
import Select from 'react-select';

const StockMovements = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    product_id: '',
    movement_type: '',
    reference_type: '',
    store_id: '',
    start_date: '',
    end_date: '',
    search: '',
    page: 1,
    limit: 50
  });
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({});
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [movementForm, setMovementForm] = useState({
    product_id: null,
    movement_type: { value: 'in', label: 'Entrée' },
    quantity: '',
    reference_type: null,
    reference_id: '',
    notes: ''
  });
  const [transferForm, setTransferForm] = useState({
    products: [{ product_id: '', quantity: '' }],
    from_store_id: '',
    to_store_id: '',
    notes: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const { hasAnyRole, user } = useAuth();

  // Add state for stock adjustment
  const [adjustingStock, setAdjustingStock] = useState(false);
  
  // Add product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Availability check modal state
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [selectedProductForAvailability, setSelectedProductForAvailability] = useState(null);
  const [availabilityProductSearch, setAvailabilityProductSearch] = useState('');
  const [availabilityProductSearchResults, setAvailabilityProductSearchResults] = useState([]);

  // Add state for filtered products in movement modal
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  /** Filtre liste produits (mouvements / modales) : facture d'achat ou non */
  const [productAchatFactureFilter, setProductAchatFactureFilter] = useState('');

  const productsByFacture = useMemo(() => {
    if (!productAchatFactureFilter) return products;
    return products.filter((p) => (p.achat_facture || 'facture') === productAchatFactureFilter);
  }, [products, productAchatFactureFilter]);

  useEffect(() => {
    fetchMovements();
    fetchProducts();
    fetchStores();
    fetchStats();
  }, [filters.product_id, filters.movement_type, filters.reference_type, filters.store_id, filters.start_date, filters.end_date, filters.search, filters.page, filters.limit]);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      await fetchProducts();
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  // Handle availability check
  const handleCheckAvailability = async (productId) => {
    try {
      setAvailabilityLoading(true);
      const response = await productsAPI.checkAvailability(productId);
      setAvailabilityData(response.data);
      setShowAvailabilityModal(true);
    } catch (error) {
      console.error('Error checking availability:', error);
      toast.error('Erreur lors de la vérification de disponibilité');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Handle availability modal open
  const handleOpenAvailabilityModal = () => {
    setAvailabilityData(null);
    setSelectedProductForAvailability(null);
    setAvailabilityProductSearch('');
    setAvailabilityProductSearchResults([]);
    setShowAvailabilityModal(true);
  };

  // Handle product search for availability
  const handleAvailabilityProductSearch = (searchTerm) => {
    setAvailabilityProductSearch(searchTerm);
    if (searchTerm.length >= 2) {
      const filtered = getFilteredProducts(searchTerm);
      setAvailabilityProductSearchResults(filtered);
    } else {
      setAvailabilityProductSearchResults([]);
    }
  };

  // Handle product filtering for movement modal
  const handleProductFilter = (searchTerm) => {
    setProductSearchTerm(searchTerm);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set loading state
    setProductSearchLoading(true);
    
    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      if (searchTerm.length >= 2) {
        const filtered = productsByFacture.filter(p => 
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.id.toString().includes(searchTerm)
        );
        setFilteredProducts(filtered);
      } else {
        setFilteredProducts(productsByFacture);
      }
      setProductSearchLoading(false);
    }, 300); // 300ms delay
    
    setSearchTimeout(newTimeout);
  };

  // Initialize filtered products when products change (respecte filtre facture achat)
  useEffect(() => {
    setFilteredProducts(productsByFacture);
  }, [productsByFacture]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const response = await stockMovementsAPI.getStockMovements(filters);
      setMovements(response.data.movements);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching movements:', error);
      toast.error('Failed to load stock movements');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getProducts();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const getFilteredProducts = (searchTerm = '') => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    return productsByFacture.filter(p => 
      p.name.toLowerCase().includes(searchLower) ||
      (p.brand && p.brand.toLowerCase().includes(searchLower)) ||
      (p.model && p.model.toLowerCase().includes(searchLower)) ||
      (p.category_name && p.category_name.toLowerCase().includes(searchLower))
    ).map(p => ({
      value: p.id,
      label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}`,
      product: p
    }));
  };

  const fetchStores = async () => {
    try {
      const response = await storesAPI.getStores();
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await stockMovementsAPI.getStockMovementStats({
        start_date: filters.start_date,
        end_date: filters.end_date
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStockAdjustment = async (movement, newMovementType) => {
    if (!hasAnyRole(['admin', 'manager'])) {
      toast.error('Vous n\'avez pas les permissions pour ajuster le stock');
      return;
    }

    setAdjustingStock(true);
    try {
      await stockMovementsAPI.createStockMovement({
        product_id: movement.product_id,
        movement_type: newMovementType,
        quantity: Math.abs(movement.quantity),
        reference_type: 'adjustment',
        notes: `Ajustement manuel - ${movement.notes || 'Aucune note'}`,
        store_id: movement.store_id
      });
      
      toast.success('Ajustement de stock effectué');
      fetchMovements();
    } catch (error) {
      console.error('Error adjusting stock:', error);
      toast.error('Erreur lors de l\'ajustement');
    } finally {
      setAdjustingStock(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleExport = async () => {
    try {
      const response = await stockMovementsAPI.exportStockMovements(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock_movements_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export réussi');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleCreateMovement = () => {
    if (products.length === 0) {
      toast.error('Veuillez attendre que les produits soient chargés');
      return;
    }
    
    setMovementForm({
      product_id: null,
      movement_type: { value: 'in', label: 'Entrée' },
      quantity: '',
      reference_type: null,
      reference_id: '',
      notes: ''
    });
    setShowMovementModal(true);
  };

  const handleTransferStock = () => {
    setTransferForm({
      products: [{ product_id: '', quantity: '' }],
      from_store_id: user?.store_id || '',
      to_store_id: '',
      notes: ''
    });
    setShowTransferModal(true);
  };

  const handleSubmitMovement = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      // Prepare the data in the correct format for the API
      const movementData = {
        product_id: movementForm.product_id?.value || movementForm.product_id,
        movement_type: movementForm.movement_type?.value || movementForm.movement_type,
        quantity: parseInt(movementForm.quantity),
        reference_type: movementForm.reference_type?.value || movementForm.reference_type || null,
        reference_id: movementForm.reference_id || null,
        notes: movementForm.notes || null
      };

      await stockMovementsAPI.createStockMovement(movementData);
      toast.success('Mouvement de stock créé');
      setShowMovementModal(false);
      // Reset form
      resetMovementForm();
      fetchMovements();
    } catch (error) {
      console.error('Error creating movement:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Erreur: ${errorMessages}`);
      } else {
        toast.error('Erreur lors de la création');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle select changes
  const handleSelectChange = (name, selectedOption) => {
    if (name === 'product_id' || name === 'from_store_id' || name === 'to_store_id') {
      setTransferForm((f) => ({ ...f, [name]: selectedOption ? selectedOption.value : '' }));
    } else {
      setMovementForm((f) => ({ ...f, [name]: selectedOption ? selectedOption.value : '' }));
    }
  };

  // Handle transfer product changes
  const handleTransferProductChange = (index, field, value) => {
    setTransferForm(prev => ({
      ...prev,
      products: prev.products.map((product, i) => 
        i === index ? { ...product, [field]: value } : product
      )
    }));
  };

  // Add new product to transfer
  const addTransferProduct = () => {
    setTransferForm(prev => ({
      ...prev,
      products: [...prev.products, { product_id: '', quantity: '' }]
    }));
  };

  // Remove product from transfer
  const removeTransferProduct = (index) => {
    setTransferForm(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    setTransferLoading(true);
    try {
      // Validate that all products have product_id and quantity
      const validProducts = transferForm.products.filter(p => p.product_id && p.quantity && p.quantity > 0);
      
      if (validProducts.length === 0) {
        toast.error('Veuillez ajouter au moins un produit avec une quantité valide');
        return;
      }

      // Prepare bulk transfer data
      const bulkTransferData = {
        products: validProducts.map(product => ({
          product_id: product.product_id,
          quantity: parseInt(product.quantity)
        })),
        from_store_id: transferForm.from_store_id,
        to_store_id: transferForm.to_store_id,
        notes: transferForm.notes || ''
      };

      // Execute bulk transfer
      const result = await stockMovementsAPI.bulkTransfer(bulkTransferData);

      toast.success(`Transfert de ${result.data.transferred_products} produit(s) effectué avec succès`);
      setShowTransferModal(false);
      fetchMovements();
      
    } catch (error) {
      console.error('Error creating bulk transfer:', error);
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.join(', ');
        toast.error(`Erreur lors du transfert: ${errorMessages}`);
      } else {
        toast.error('Erreur lors du transfert');
      }
    } finally {
      setTransferLoading(false);
    }
  };

  const handleViewMovement = (movement) => {
    setSelectedMovement(movement);
    setShowViewModal(true);
  };

  const getMovementTypeLabel = (type) => {
    const labels = {
      'in': 'Entrée',
      'out': 'Sortie',
      'adjustment': 'Ajustement',
      'transfer': 'Transfert'
    };
    return labels[type] || type;
  };

  const getMovementTypeVariant = (type) => {
    const variants = {
      'in': 'success',
      'out': 'danger',
      'adjustment': 'warning',
      'transfer': 'info'
    };
    return variants[type] || 'default';
  };

  const getReferenceTypeLabel = (type) => {
    const labels = {
      'manual': 'Manuel',
      'purchase': 'Achat',
      'sale': 'Vente',
      'sales_order': 'Commande de vente',
      'transfer': 'Transfert',
      'adjustment': 'Ajustement'
    };
    return labels[type] || type;
  };

  const translateNotes = (notes) => {
    if (!notes) return '-';
    
    // Translate common English terms to French
    let translatedNotes = notes;
    
    // Replace English terms with French equivalents
    translatedNotes = translatedNotes.replace(/sales_order/g, 'Commande de vente');
    translatedNotes = translatedNotes.replace(/Quantity:/g, 'Quantité:');
    translatedNotes = translatedNotes.replace(/Cancellation sales_order/g, 'Annulation commande de vente');
    translatedNotes = translatedNotes.replace(/unit\(s\)/g, 'unité(s)');
    translatedNotes = translatedNotes.replace(/unit/g, 'unité');
    
    return translatedNotes.length > 50 ? `${translatedNotes.substring(0, 50)}...` : translatedNotes;
  };

  const movementTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'in', label: 'Entrée' },
    { value: 'out', label: 'Sortie' },
    { value: 'adjustment', label: 'Ajustement' },
    { value: 'transfer', label: 'Transfert' }
  ];

  const referenceTypeOptions = [
    { value: '', label: 'Toutes les références' },
    { value: 'manual', label: 'Manuel' },
    { value: 'purchase', label: 'Achat' },
    { value: 'sale', label: 'Vente' },
    { value: 'sales_order', label: 'Commande de vente' },
    { value: 'transfer', label: 'Transfert' },
    { value: 'adjustment', label: 'Ajustement' }
  ];

  const tableColumns = [
    {
      key: 'product_name',
      label: 'Produit',
      render: (value, movement) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Package className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{movement.product_name}</div>
            <div className="text-sm text-neutral-500">{movement.product_brand || ''}</div>
          </div>
        </div>
      )
    },
    {
      key: 'movement_type',
      label: 'Type',
      render: (value) => (
        <ModernBadge variant={getMovementTypeVariant(value)}>
          {getMovementTypeLabel(value)}
        </ModernBadge>
      )
    },
    {
      key: 'quantity',
      label: 'Quantité',
      render: (value, movement) => (
        <span className={`font-semibold ${movement.movement_type === 'in' ? 'text-success-600' : 'text-danger-600'}`}>
          {movement.movement_type === 'in' ? '+' : '-'}{value}
        </span>
      )
    },
    {
      key: 'store_name',
      label: 'Magasin',
      render: (value) => (
        <div className="flex items-center">
          <Store className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'reference_type',
      label: 'Référence',
      render: (value, movement) => (
        <div>
          <div className="font-medium text-neutral-700">
            {getReferenceTypeLabel(value)}
          </div>
          {movement.reference_id && (
            <div className="text-sm text-neutral-500">#{movement.reference_id}</div>
          )}
        </div>
      )
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (value) => (
        <span className="text-sm text-neutral-600" title={value}>
          {translateNotes(value)}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{new Date(value).toLocaleDateString('fr-FR')}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, movement) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => handleViewMovement(movement)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="success"
            size="sm"
            icon={<CheckCircle className="w-4 h-4" />}
            onClick={() => handleCheckAvailability(movement.product_id)}
            title="Vérifier la disponibilité dans tous les magasins"
          >
            Disponibilité
          </ModernButton>
          {hasAnyRole(['admin', 'manager']) && movement.movement_type === 'out' && (
            <ModernButton
              variant="warning"
              size="sm"
              icon={<RefreshCw className="w-4 h-4" />}
              onClick={() => handleStockAdjustment(movement, 'in')}
              disabled={adjustingStock}
            >
              Ajuster
            </ModernButton>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  // Reset movement form and search state
  const resetMovementForm = () => {
    setMovementForm({
      product_id: null,
      movement_type: { value: 'in', label: 'Entrée' },
      quantity: '',
      reference_type: null,
      reference_id: '',
      notes: ''
    });
    setProductSearchTerm('');
    setFilteredProducts(productsByFacture);
  };

  // Handle modal close
  const handleCloseMovementModal = () => {
    setShowMovementModal(false);
    resetMovementForm();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Mouvements de Stock</h1>
          <p className="text-neutral-600">Suivi des entrées, sorties et transferts de stock</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des mouvements..."
            onSearch={(term) => handleFilterChange('search', term)}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="ghost"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => {
              fetchMovements();
              fetchStats();
            }}
            disabled={loading}
          >
            Actualiser
          </ModernButton>
          <ModernButton
            variant="ghost"
            icon={<Filter className="w-4 h-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtres
          </ModernButton>
          <ModernButton
            variant="ghost"
            icon={<Download className="w-4 h-4" />}
            onClick={handleExport}
          >
            Exporter
          </ModernButton>
          {hasAnyRole(['admin', 'manager']) && (
            <>
              <ModernButton
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={handleCreateMovement}
              >
                Nouveau mouvement
              </ModernButton>
              <ModernButton
                variant="info"
                icon={<Truck className="w-4 h-4" />}
                onClick={handleTransferStock}
              >
                Transfert
              </ModernButton>
              <ModernButton
                variant="success"
                icon={<CheckCircle className="w-4 h-4" />}
                onClick={handleOpenAvailabilityModal}
              >
                Vérifier disponibilité
              </ModernButton>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total des Mouvements</p>
              <p className="text-2xl font-bold text-neutral-900">{stats.stats?.total_movements || 0}</p>
              <p className="text-xs text-neutral-500">Tous types confondus</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Entrées en Stock</p>
              <p className="text-2xl font-bold text-success-600">
                {stats.stats?.total_in || 0}
              </p>
              <p className="text-xs text-neutral-500">Achats, réceptions, retours</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Sorties de Stock</p>
              <p className="text-2xl font-bold text-danger-600">
                {stats.stats?.total_out || 0}
              </p>
              <p className="text-xs text-neutral-500">Ventes, commandes, transferts</p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Ajustements Manuels</p>
              <p className="text-2xl font-bold text-warning-600">
                {stats.stats?.total_adjustments || 0}
              </p>
              <p className="text-xs text-neutral-500">Corrections, inventaires</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Filters */}
      {showFilters && (
        <ModernCard className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ModernSelect
              label="Facture achat (liste produits)"
              value={[
                { value: '', label: 'Tous' },
                { value: 'facture', label: 'Avec facture achat' },
                { value: 'non_facture', label: 'Sans facture achat' },
              ].find((o) => o.value === productAchatFactureFilter) || null}
              onChange={(selectedOption) => setProductAchatFactureFilter(selectedOption ? selectedOption.value : '')}
              options={[
                { value: '', label: 'Tous' },
                { value: 'facture', label: 'Avec facture achat' },
                { value: 'non_facture', label: 'Sans facture achat' },
              ]}
            />
            <ModernSelect
              label="Produit"
              value={productsByFacture.map(p => ({ value: p.id, label: p.name })).find(opt => opt.value === filters.product_id) || null}
              onChange={(selectedOption) => handleFilterChange('product_id', selectedOption ? selectedOption.value : '')}
              options={[
                { value: '', label: 'Tous les produits' },
                ...productsByFacture.map(p => ({ value: p.id, label: p.name }))
              ]}
            />
            <ModernSelect
              label="Type de mouvement"
              value={movementTypeOptions.find(opt => opt.value === filters.movement_type) || null}
              onChange={(selectedOption) => handleFilterChange('movement_type', selectedOption ? selectedOption.value : '')}
              options={movementTypeOptions}
            />
            <ModernSelect
              label="Type de référence"
              value={referenceTypeOptions.find(opt => opt.value === filters.reference_type) || null}
              onChange={(selectedOption) => handleFilterChange('reference_type', selectedOption ? selectedOption.value : '')}
              options={referenceTypeOptions}
            />
            {user?.role === 'admin' && (
              <ModernSelect
                label="Magasin"
                value={stores.map(s => ({ value: s.id, label: s.name })).find(opt => opt.value === filters.store_id) || null}
                onChange={(selectedOption) => handleFilterChange('store_id', selectedOption ? selectedOption.value : '')}
                options={[
                  { value: '', label: 'Tous les magasins' },
                  ...stores.map(s => ({ value: s.id, label: s.name }))
                ]}
              />
            )}
            <ModernInput
              label="Date de début"
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
            <ModernInput
              label="Date de fin"
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
            <ModernInput
              label="Recherche"
              type="text"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Rechercher dans les notes, produits..."
            />
          </div>
          <div className="flex items-center justify-end mt-4 pt-4 border-t border-neutral-200">
            <ModernButton
              variant="secondary"
              onClick={() => {
                setProductAchatFactureFilter('');
                setFilters({
                  product_id: '',
                  movement_type: '',
                  reference_type: '',
                  store_id: '',
                  start_date: '',
                  end_date: '',
                  search: '',
                  page: 1,
                  limit: 50
                });
              }}
            >
              Réinitialiser les filtres
            </ModernButton>
          </div>
        </ModernCard>
      )}

      {/* Movements Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des mouvements</h3>
            <p className="text-sm text-neutral-600">{movements.length} mouvements trouvés</p>
          </div>
          <div className="flex items-center space-x-3">
            <ModernButton
              variant="secondary"
              onClick={fetchProducts}
              disabled={products.length === 0}
              icon={<RefreshCw className="w-4 h-4" />}
            >
              Actualiser les produits ({products.length})
            </ModernButton>
            <ModernButton
              variant="primary"
              onClick={handleCreateMovement}
              disabled={products.length === 0}
              icon={<Plus className="w-4 h-4" />}
            >
              Nouveau mouvement
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={movements}
          className="w-full"
        />

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-neutral-600">
              Page {pagination.page} sur {pagination.pages} ({pagination.total} résultats)
            </div>
            <div className="flex items-center space-x-2">
              <ModernButton
                variant="ghost"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(1)}
              >
                Première
              </ModernButton>
              <ModernButton
                variant="ghost"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Précédent
              </ModernButton>
              <ModernButton
                variant="ghost"
                size="sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Suivant
              </ModernButton>
              <ModernButton
                variant="ghost"
                size="sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => handlePageChange(pagination.pages)}
              >
                Dernière
              </ModernButton>
            </div>
          </div>
        )}
      </ModernCard>

      {/* Create Movement Modal */}
      <ModernModal
        isOpen={showMovementModal}
        onClose={handleCloseMovementModal}
        title="Nouveau mouvement de stock"
        size="md"
      >
        <form onSubmit={handleSubmitMovement} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Rechercher un produit
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Tapez pour filtrer les produits..."
                value={productSearchTerm}
                onChange={(e) => handleProductFilter(e.target.value)}
                className="w-full px-3 py-2 pr-16 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {productSearchLoading && (
                <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
              )}
              {productSearchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setProductSearchTerm('');
                    setFilteredProducts(products);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Produit *
            </label>
            <Select
              classNamePrefix="modern-select"
              value={movementForm.product_id}
              onChange={(selectedOption) => setMovementForm(prev => ({ ...prev, product_id: selectedOption }))}
              options={filteredProducts.length > 0 ? filteredProducts.map(p => ({ value: p.id, label: p.name })) : []}
              isSearchable
              isClearable
              placeholder={productsByFacture.length === 0 ? "Chargement des produits..." : "Sélectionnez un produit..."}
              noOptionsMessage={() => "Aucun produit trouvé"}
              loadingMessage={() => "Chargement..."}
              isLoading={products.length === 0}
              filterOption={() => true} // Disable built-in filtering since we're doing it manually
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af'
                  }
                }),
                menu: (base) => ({
                  ...base,
                  maxHeight: '300px',
                  zIndex: 9999
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#eff6ff' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6'
                  }
                })
              }}
              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
              required
            />
            <p className="text-xs text-neutral-500">
              {filteredProducts.length === productsByFacture.length 
                ? `Tous les ${productsByFacture.length} produits` 
                : `${filteredProducts.length} produit(s) trouvé(s) sur ${productsByFacture.length}`
              }
            </p>
          </div>
          <ModernSelect
            label="Type de mouvement"
            value={movementForm.movement_type}
            onChange={(selectedOption) => {
              setMovementForm(prev => ({ ...prev, movement_type: selectedOption }));
            }}
            options={[
              { value: 'in', label: 'Entrée' },
              { value: 'out', label: 'Sortie' },
              { value: 'adjustment', label: 'Ajustement' },
              { value: 'transfer', label: 'Transfert' }
            ]}
            required
          />
          <ModernInput
            label="Quantité"
            type="number"
            value={movementForm.quantity}
            onChange={(e) => setMovementForm(prev => ({ ...prev, quantity: e.target.value }))}
            placeholder="0"
            required
          />
          <ModernSelect
            label="Type de référence"
            value={movementForm.reference_type}
            onChange={(selectedOption) => {
              setMovementForm(prev => ({ ...prev, reference_type: selectedOption }));
            }}
            options={[
              { value: null, label: 'Aucune' },
              { value: 'manual', label: 'Manuel' },
              { value: 'purchase', label: 'Achat' },
              { value: 'sale', label: 'Vente' },
              { value: 'transfer', label: 'Transfert' }
            ]}
          />
          <ModernInput
            label="ID de référence"
            type="number"
            value={movementForm.reference_id}
            onChange={(e) => setMovementForm(prev => ({ ...prev, reference_id: e.target.value }))}
            placeholder="Optionnel"
          />
          <ModernInput
            label="Notes"
            value={movementForm.notes}
            onChange={(e) => setMovementForm(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes optionnelles"
          />

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={handleCloseMovementModal}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              disabled={submitLoading}
            >
              {submitLoading ? 'Création...' : 'Créer le mouvement'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* Transfer Stock Modal */}
      <ModernModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title="Transfert de stock entre magasins"
        size="full"
      >
        <form onSubmit={handleSubmitTransfer} className="space-y-6">
          {/* Products Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-neutral-900">Produits à transférer</h3>
              <ModernButton
                type="button"
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={addTransferProduct}
              >
                Ajouter un produit
              </ModernButton>
            </div>
            
            {transferForm.products.map((product, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 border border-neutral-200 rounded-lg">
                <div className="flex-1">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-neutral-700">
                      {`Produit ${index + 1}`}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        className={`w-full px-4 py-3 pr-20 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${product.product_id ? 'bg-neutral-50 cursor-default' : ''}`}
                        value={product.product_id ? products.find(p => p.id == product.product_id)?.name || '' : (productSearchTerms[index] || '')}
                        title={product.product_id ? products.find(p => p.id == product.product_id)?.name || '' : ''}
                        onChange={(e) => {
                          if (product.product_id) return; // Don't allow changes when product is selected
                          
                          const searchTerm = e.target.value;
                          setProductSearchTerms(prev => ({ ...prev, [index]: searchTerm }));
                          
                          if (searchTerm.length >= 2) {
                            const filtered = getFilteredProducts(searchTerm);
                            setProductSearchResults(filtered);
                          } else {
                            setProductSearchResults([]);
                          }
                        }}
                        onFocus={() => {
                          if (!product.product_id) {
                            setProductSearchTerms(prev => ({ ...prev, [index]: '' }));
                          }
                        }}
                        onBlur={() => {
                          // Delay hiding results to allow clicking
                          setTimeout(() => setProductSearchResults([]), 200);
                        }}
                        readOnly={product.product_id}
                        required
                      />
                      {!product.product_id && (
                        <button
                          type="button"
                          onClick={() => setShowAddProductModal(true)}
                          className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                          title="Ajouter un nouveau produit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      )}
                      <div className="absolute right-3 top-3 text-neutral-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      {product.product_id && (
                        <button
                          type="button"
                          onClick={() => {
                            handleTransferProductChange(index, 'product_id', '');
                            setProductSearchTerms(prev => ({ ...prev, [index]: '' }));
                          }}
                          className="absolute right-10 top-3 text-neutral-400 hover:text-neutral-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      {productSearchLoading && (
                        <div className="absolute right-10 top-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                        </div>
                      )}
                      {/* Search Results Dropdown */}
                      {productSearchTerms[index] && productSearchTerms[index].length >= 2 && !product.product_id && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {productSearchResults.length > 0 ? (
                            <div className="divide-y divide-neutral-200">
                              {productSearchResults.map((option) => (
                                <div
                                  key={option.value}
                                  onClick={() => {
                                    handleTransferProductChange(index, 'product_id', option.value);
                                    setProductSearchResults([]);
                                    setProductSearchTerms(prev => ({ ...prev, [index]: '' }));
                                  }}
                                  className="p-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-neutral-900 break-words">
                                        {option.product.name}
                                      </div>
                                      <div className="text-sm text-neutral-600 break-words">
                                        {option.product.brand && `${option.product.brand}`}
                                        {option.product.model && ` - ${option.product.model}`}
                                        {option.product.category_name && ` (${option.product.category_name})`}
                                      </div>
                                      <div className="text-sm text-success-600 font-semibold">
                                        Stock: {option.product.stock}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-4 text-center text-neutral-500">
                              Aucun produit trouvé
                            </div>
                          )}
                        </div>
                      )}
                      
                                            {/* Placeholder when typing less than 2 characters */}
                      {productSearchTerms[index] && productSearchTerms[index].length > 0 && productSearchTerms[index].length < 2 && !product.product_id && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg p-8 text-center text-neutral-500">
                          <svg className="w-12 h-12 mx-auto mb-3 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-lg font-medium mb-2">Recherchez un produit</p>
                          <p className="text-sm">Commencez à taper pour rechercher parmi vos produits</p>
                        </div>
                      )}
                     </div>
                     
                     {/* Selected Product Info */}
                     {product.product_id && (
                       <div className="mt-2 p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                         <div className="text-xs text-neutral-600 mb-1">Produit sélectionné:</div>
                         <div className="text-sm font-medium text-neutral-900 break-words">
                           {products.find(p => p.id == product.product_id)?.name || ''}
                         </div>
                         <div className="text-xs text-neutral-600">
                           Stock disponible: {products.find(p => p.id == product.product_id)?.stock || 0}
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
                <div className="flex-1">
                  <ModernInput
                    label="Quantité"
                    type="number"
                    min="1"
                    value={product.quantity}
                    onChange={(e) => handleTransferProductChange(index, 'quantity', e.target.value)}
                    placeholder="0"
                    required
                  />
                </div>
                {transferForm.products.length > 1 && (
                  <div className="flex items-end">
                    <ModernButton
                      type="button"
                      variant="danger"
                      size="sm"
                      icon={<X className="w-4 h-4" />}
                      onClick={() => removeTransferProduct(index)}
                    >
                      Supprimer
                    </ModernButton>
                  </div>
                )}
              </div>
            ))}
          </div>
          <ModernInput
            label="Magasin source"
            value={stores.find(s => s.id == user?.store_id)?.name || 'Magasin non défini'}
            disabled={true}
            className="bg-gray-50"
          />
          <ModernSelect
            label="Magasin destination"
            value={stores.map(s => ({ value: s.id, label: s.name })).find(opt => opt.value === transferForm.to_store_id) || null}
            onChange={(selectedOption) => handleSelectChange('to_store_id', selectedOption)}
            options={stores.filter(s => s.id != user?.store_id).map(s => ({ value: s.id, label: s.name }))}
            required
          />
          <ModernInput
            label="Notes"
            value={transferForm.notes}
            onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes optionnelles sur le transfert"
          />

          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <div className="flex items-start">
              <Truck className="w-5 h-5 text-info-600 mt-0.5 mr-3" />
              <div>
                <h4 className="font-medium text-info-900">Information sur le transfert en masse</h4>
                <p className="text-sm text-info-700 mt-1">
                  Ce transfert créera automatiquement deux mouvements de stock par produit : 
                  une sortie du magasin source et une entrée dans le magasin destination.
                  Vous pouvez transférer plusieurs produits en même temps.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowTransferModal(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="info"
              disabled={transferLoading || transferForm.from_store_id === transferForm.to_store_id}
            >
              {transferLoading ? 'Transfert en cours...' : 'Effectuer le transfert'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* View Movement Modal */}
      <ModernModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Détails du mouvement"
        size="lg"
      >
        {selectedMovement && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-neutral-600">Détails du mouvement</p>
                <p className="text-lg font-bold text-neutral-900">{selectedMovement.product_name}</p>
                <p className="text-sm text-neutral-500">{selectedMovement.product_brand || ''}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Type de mouvement</p>
                <ModernBadge variant={getMovementTypeVariant(selectedMovement.movement_type)}>
                  {getMovementTypeLabel(selectedMovement.movement_type)}
                </ModernBadge>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Quantité</p>
                <p className="text-lg font-bold text-neutral-900">{selectedMovement.quantity}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Référence</p>
                <p className="text-lg font-bold text-neutral-900">{getReferenceTypeLabel(selectedMovement.reference_type)}</p>
                {selectedMovement.reference_id && <p className="text-sm text-neutral-500">#{selectedMovement.reference_id}</p>}
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Notes</p>
                <p className="text-lg text-neutral-900">{selectedMovement.notes || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Magasin</p>
                <p className="text-lg font-bold text-neutral-900">{selectedMovement.store_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Date</p>
                <p className="text-lg font-bold text-neutral-900">{new Date(selectedMovement.created_at).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
          </div>
        )}
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>

      {/* Availability Check Modal */}
      <ModernModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        title="Vérifier la disponibilité d'un produit"
        size="full"
      >
        <div className="space-y-6 h-[85vh] overflow-y-auto px-4">
          {/* Product Search Section */}
          {!availabilityData && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Rechercher un produit
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Tapez le nom, marque ou modèle du produit..."
                    className="w-full px-4 py-3 pr-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={availabilityProductSearch}
                    onChange={(e) => handleAvailabilityProductSearch(e.target.value)}
                  />
                  <div className="absolute right-3 top-3 text-neutral-400">
                    <Search className="w-5 h-5" />
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {availabilityProductSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      <div className="divide-y divide-neutral-200">
                        {availabilityProductSearchResults.map((option) => (
                          <div
                            key={option.value}
                            onClick={() => {
                              setSelectedProductForAvailability(option.product);
                              setAvailabilityProductSearchResults([]);
                              setAvailabilityProductSearch(option.label);
                            }}
                            className="p-3 cursor-pointer hover:bg-neutral-50 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-neutral-900 break-words">
                                  {option.product.name}
                                </div>
                                <div className="text-sm text-neutral-600 break-words">
                                  {option.product.brand && `${option.product.brand}`}
                                  {option.product.model && ` - ${option.product.model}`}
                                  {option.product.category_name && ` (${option.product.category_name})`}
                                </div>
                                <div className="text-sm text-success-600 font-semibold">
                                  Stock actuel: {option.product.stock}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedProductForAvailability && (
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-neutral-900">
                        {selectedProductForAvailability.name}
                      </h4>
                      <p className="text-sm text-neutral-600">
                        {selectedProductForAvailability.brand && `${selectedProductForAvailability.brand}`}
                        {selectedProductForAvailability.model && ` - ${selectedProductForAvailability.model}`}
                      </p>
                    </div>
                    <ModernButton
                      variant="primary"
                      onClick={() => handleCheckAvailability(selectedProductForAvailability.id)}
                      disabled={availabilityLoading}
                    >
                      {availabilityLoading ? 'Vérification...' : 'Vérifier la disponibilité'}
                    </ModernButton>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Availability Results */}
          {availabilityData && (
            <div className="space-y-6">
              {/* Product Info */}
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                <div className="flex items-center">
                  <Package className="w-6 h-6 text-primary-600 mr-3" />
                  <div>
                    <h3 className="font-semibold text-primary-900">
                      {availabilityData.product.name}
                    </h3>
                    <p className="text-sm text-primary-700">
                      {availabilityData.product.brand && `${availabilityData.product.brand}`}
                      {availabilityData.product.model && ` - ${availabilityData.product.model}`}
                      {availabilityData.product.category_name && ` (${availabilityData.product.category_name})`}
                    </p>
                    {availabilityData.availability.filter(store => store.has_product).length > 1 && (
                      <p className="text-xs text-primary-600 mt-1">
                        ✓ Produit disponible dans {availabilityData.availability.filter(store => store.has_product).length} magasins
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ModernCard className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success-600">
                      {availabilityData.total_stock}
                    </div>
                    <div className="text-sm text-neutral-600">Stock total</div>
                  </div>
                </ModernCard>
                <ModernCard className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-info-600">
                      {availabilityData.store_count}
                    </div>
                    <div className="text-sm text-neutral-600">Magasins</div>
                  </div>
                </ModernCard>
                <ModernCard className="p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-warning-600">
                      {availabilityData.stores_with_stock}
                    </div>
                    <div className="text-sm text-neutral-600">Avec stock</div>
                  </div>
                </ModernCard>
              </div>

              {/* Store Availability Table */}
              <div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                  Disponibilité par magasin
                </h4>
                <div className="space-y-3">
                  {availabilityData.availability.map((store) => (
                    <div
                      key={store.store_id}
                      className={`p-4 rounded-lg border ${
                        store.stock > 0
                          ? 'bg-success-50 border-success-200'
                          : 'bg-neutral-50 border-neutral-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Store className="w-5 h-5 text-neutral-400 mr-3" />
                          <div>
                            <div className="font-medium text-neutral-900">
                              {store.store_name}
                            </div>
                            <div className="text-sm text-neutral-600">
                              {store.has_product ? 'Produit disponible' : 'Produit non disponible'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          {store.stock > 0 ? (
                            <CheckCircle className="w-5 h-5 text-success-600 mr-2" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-neutral-400 mr-2" />
                          )}
                          <span className={`font-semibold ${
                            store.stock > 0 ? 'text-success-600' : 'text-neutral-500'
                          }`}>
                            {store.stock} unités
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200">
                <ModernButton
                  variant="secondary"
                  onClick={() => {
                    setAvailabilityData(null);
                    setSelectedProductForAvailability(null);
                    setAvailabilityProductSearch('');
                  }}
                >
                  Vérifier un autre produit
                </ModernButton>
                <ModernButton
                  variant="primary"
                  onClick={() => setShowAvailabilityModal(false)}
                >
                  Fermer
                </ModernButton>
              </div>
            </div>
          )}
        </div>
      </ModernModal>
    </div>
  );
};

export default StockMovements; 